import type {
  RichTextEditorLabels,
  RichTextEditorUpload,
  RichTextEditorUploadRules,
  UploadFn,
} from '@wundo/ui';
import { MAX_MEETING_DOCUMENT_BYTES } from '@wundo/shared/attachments.ts';
import {
  MAX_MEETING_ATTACHMENT_FILE_BYTES,
  MAX_MEETING_ATTACHMENT_IMAGE_BYTES,
  MEETING_ATTACHMENT_FILE_MIME_TYPES,
  MEETING_ATTACHMENT_IMAGE_MIME_TYPES,
  type MeetingAttachmentResponse,
} from '@wundo/shared/meetingAttachments.ts';
import { apiFetchJson } from '@wundo/client/api.ts';
import { THREAD_DOCUMENT_MIME_TYPES, uploadThreadDocument } from './meetingPage.ts';
import { m } from '@/paraglide/messages.js';

/**
 * Everything the design system's editor needs from the app, bundled once: the
 * copy in the user's language, and the upload path into this user's attachment
 * library. Built in the meeting view and passed down, so the three surfaces
 * cannot drift apart in what their editor offers.
 */
export interface RichTextEditorWiring {
  labels: RichTextEditorLabels;
  onUpload: UploadFn;
  uploadRules: RichTextEditorUploadRules;
  isAttachmentHref: (href: string) => boolean;
}

/** Who is writing, and what the document is about. */
export interface RichTextEditorWiringOptions {
  userId: string;
  /**
   * The conversation this document belongs to, when it belongs to one. With a
   * conversation, a file dropped into the text is a document of it; without,
   * it is what it has always been — an attachment of this one page.
   */
  threadId?: string | null;
  /** Said when a dropped file became a document of the conversation. */
  onDocumentAdded?: () => void;
}

const uploadRules: RichTextEditorUploadRules = {
  imageTypes: [...MEETING_ATTACHMENT_IMAGE_MIME_TYPES],
  fileTypes: [...MEETING_ATTACHMENT_FILE_MIME_TYPES],
  maxImageBytes: MAX_MEETING_ATTACHMENT_IMAGE_BYTES,
  maxFileBytes: MAX_MEETING_ATTACHMENT_FILE_BYTES,
};

/**
 * Which links in a stored document are this app's own attachments. Matched on
 * the route rather than the host, so the same document works against every
 * environment's API origin — and on both routes a file can arrive by, since a
 * chip is a chip whether the file became a document or stayed an attachment.
 */
const isAttachmentHref = (href: string): boolean =>
  href.includes('/meeting-attachments/') ||
  (href.includes('/meeting-threads/') && href.includes('/documents/'));

/**
 * Whether this file is one Wundo can take as a document of the conversation.
 * Pictures are not — they illustrate the text, they are not read as material —
 * and neither is a file over the document cap, which the attachment library
 * takes at a size this route would refuse.
 */
const isDocument = (file: File): boolean =>
  THREAD_DOCUMENT_MIME_TYPES.includes(file.type) &&
  file.size <= MAX_MEETING_DOCUMENT_BYTES;

/** Put one pasted or dropped file into the user's attachment library. */
async function uploadAttachment(
  userId: string,
  file: File,
): Promise<RichTextEditorUpload> {
  const body = new FormData();
  body.append('file', file);
  const attachment = await apiFetchJson<MeetingAttachmentResponse>(
    `/users/${userId}/meeting-attachments`,
    { method: 'POST', body },
  );
  return { url: attachment.url, kind: attachment.kind, name: attachment.name };
}

/**
 * Where one pasted or dropped file goes.
 *
 * On a conversation, a readable file is a document of it: the same store the
 * Bronnen panel adds to, so that dropping a policy into the preparation and
 * handing it over in the panel come to the same thing. Everything else — a
 * picture, a file too big to be read — stays an attachment of this page. Either
 * way the answer carries the auth-proxied URL the document embeds.
 */
const uploadFile =
  ({ userId, threadId, onDocumentAdded }: RichTextEditorWiringOptions): UploadFn =>
  async (file: File): Promise<RichTextEditorUpload> => {
    if (!threadId || !isDocument(file)) {
      return await uploadAttachment(userId, file);
    }

    const added = await uploadThreadDocument(userId, threadId, file);
    onDocumentAdded?.();
    return { url: added.url, kind: 'file', name: added.name };
  };

export function buildRichTextEditorWiring(
  options: RichTextEditorWiringOptions,
): RichTextEditorWiring {
  return {
    labels: {
      heading1: m.rte_heading1(),
      heading2: m.rte_heading2(),
      heading3: m.rte_heading3(),
      bulletList: m.rte_bullet_list(),
      orderedList: m.rte_ordered_list(),
      taskList: m.rte_task_list(),
      quote: m.rte_quote(),
      codeBlock: m.rte_code_block(),
      divider: m.rte_divider(),
      image: m.rte_image(),
      file: m.rte_file(),
      noResults: m.rte_no_results(),
      bold: m.rte_bold(),
      italic: m.rte_italic(),
      strike: m.rte_strike(),
      code: m.rte_code(),
      link: m.rte_link(),
      linkPlaceholder: m.rte_link_placeholder(),
      removeLink: m.rte_remove_link(),
      addBlock: m.rte_add_block(),
      dragHandle: m.rte_drag_handle(),
      uploading: m.rte_uploading(),
      uploadFailed: m.rte_upload_failed(),
      uploadUnsupported: m.rte_upload_unsupported(),
      removeUpload: m.rte_remove_upload(),
    },
    onUpload: uploadFile(options),
    uploadRules,
    isAttachmentHref,
  };
}
